import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Search, RefreshCw } from 'lucide-react';

// Use env so you can point at the backend when proxy doesn't apply (e.g. production build or proxy not forwarding).
// Leave unset to use relative /api (proxy in dev). Set to e.g. http://localhost:5001 to call backend directly (backend must allow CORS).
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? '';

export const ADMIN_ROUTE = '/admin-awm-78f12c';

const ADMIN_PASSWORD = 'awm@2025!';
const STORAGE_KEY = 'adminAuthed';

type ContactStatus = 'new' | 'read' | 'replied' | 'archived';

interface ContactItem {
  id: string;
  name: string;
  mobileNumber: string;
  city: string;
  message: string;
  status: ContactStatus;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
  updatedAt: string;
  formattedDate: string;
}

interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalContacts: number;
  hasNext: boolean;
  hasPrev: boolean;
  limit: number;
}

interface DashboardResponse {
  success: boolean;
  data: ContactItem[];
  pagination: PaginationInfo;
}

const AdminPortal: React.FC = () => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isAuthed, setIsAuthed] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  // Contact dashboard state
  const [contacts, setContacts] = useState<ContactItem[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [statusFilter] = useState<'' | ContactStatus>('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sortBy] = useState<'createdAt' | 'updatedAt' | 'name' | 'city' | 'status'>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const isPasswordValid = useMemo(() => password.trim().length > 0, [password]);

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', String(Math.min(limit, 100)));
      if (statusFilter) params.set('status', statusFilter);
      if (search.trim()) params.set('search', search.trim());
      params.set('sortBy', sortBy);
      params.set('sortOrder', sortOrder);
      const res = await fetch(`${API_BASE_URL}/api/contact/dashboard?${params.toString()}`);
      const text = await res.text();
      if (text.trimStart().startsWith('<')) {
        throw new Error(
          'Server returned HTML instead of JSON. Check that the backend is running and the route GET /api/contact/dashboard exists.'
        );
      }
      let json: DashboardResponse;
      try {
        json = JSON.parse(text) as DashboardResponse;
      } catch {
        throw new Error('Invalid JSON from server.');
      }
      if (!res.ok || !json.success) {
        const errMsg = json && typeof (json as unknown as { message?: string }).message === 'string'
          ? (json as unknown as { message: string }).message
          : 'Failed to load contacts';
        throw new Error(errMsg);
      }
      setContacts(json.data);
      setPagination(json.pagination);
    } catch (err) {
      setFetchError(err instanceof Error ? err.message : 'Failed to load contacts');
      setContacts([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, [page, limit, statusFilter, search, sortBy, sortOrder]);

  useEffect(() => {
    if (isAuthed) fetchContacts();
  }, [isAuthed, fetchContacts]);

  // Auto search: debounce searchInput and apply after user stops typing
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      try {
        sessionStorage.setItem(STORAGE_KEY, 'true');
      } catch {}
      setIsAuthed(true);
      setError(null);
    } else {
      setError('Incorrect password');
    }
  };

  const handleSignOut = () => {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {}
    setIsAuthed(false);
    setPassword('');
    setError(null);
  };

  const applySearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  if (!isAuthed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-white px-4">
        <div className="w-full max-w-sm">
          <div className="bg-zinc-900 rounded-xl p-6 shadow-xl border border-zinc-800">
            <h1 className="text-xl font-semibold mb-4 text-center">Admin Access</h1>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="password" className="block text-sm mb-2">Password</label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-lg bg-zinc-800 border border-zinc-700 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-zinc-500"
                  placeholder="Enter password"
                  autoComplete="off"
                />
              </div>
              {error && <p className="text-red-400 text-sm">{error}</p>}
              <button
                type="submit"
                disabled={!isPasswordValid}
                className="w-full bg-white text-black rounded-lg py-2 font-medium disabled:opacity-50"
              >
                Enter
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white px-6 py-10">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl font-semibold">Admin Dashboard</h1>
          <button onClick={handleSignOut} className="text-sm text-zinc-300 hover:text-white">Sign out</button>
        </div>
        <div className="grid gap-6">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <h2 className="text-lg font-medium mb-2">Welcome</h2>
            <p className="text-zinc-300">You now have access to the admin portal.</p>
          </div>

          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <h2 className="text-lg font-medium mb-4">Contact submissions</h2>
            <div className="flex flex-wrap gap-3 mb-4">
              <div className="relative flex items-center w-56">
                <Search className="absolute left-3 w-4 h-4 text-zinc-500" aria-hidden />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && applySearch()}
                  placeholder="Search name, message, city, phone..."
                  className="w-full rounded-lg bg-zinc-800 border border-zinc-700 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-500"
                />
              </div>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                className="rounded-lg bg-zinc-800 border border-zinc-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-500"
              >
                <option value="desc">Newest first</option>
                <option value="asc">Oldest first</option>
              </select>
              <select
                value={limit}
                onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                className="rounded-lg bg-zinc-800 border border-zinc-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-500"
              >
                <option value={10}>10 per page</option>
                <option value={20}>20 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
              </select>
              <button
                type="button"
                onClick={() => fetchContacts()}
                disabled={loading}
                className="rounded-lg bg-zinc-700 p-2 hover:bg-zinc-600 disabled:opacity-50"
                aria-label="Refresh"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
            {fetchError && (
              <p className="text-red-400 text-sm mb-4">{fetchError}</p>
            )}
            {loading && contacts.length === 0 ? (
              <p className="text-zinc-400">Loading contacts…</p>
            ) : contacts.length === 0 ? (
              <p className="text-zinc-400">No contacts found.</p>
            ) : (
              <>
                <div className="overflow-x-auto -mx-2">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="border-b border-zinc-700">
                        <th className="text-left py-2 px-2 font-medium text-zinc-300">Date</th>
                        <th className="text-left py-2 px-2 font-medium text-zinc-300">Name</th>
                        <th className="text-left py-2 px-2 font-medium text-zinc-300">Phone</th>
                        <th className="text-left py-2 px-2 font-medium text-zinc-300">City</th>
                        <th className="text-left py-2 px-2 font-medium text-zinc-300">Message</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contacts.map((c) => (
                        <tr key={c.id} className="border-b border-zinc-800 hover:bg-zinc-800/50">
                          <td className="py-2 px-2 text-zinc-300 whitespace-nowrap">{c.formattedDate}</td>
                          <td className="py-2 px-2">{c.name}</td>
                          <td className="py-2 px-2 text-zinc-300">{c.mobileNumber}</td>
                          <td className="py-2 px-2 text-zinc-300">{c.city}</td>
                          <td className="py-2 px-2 max-w-xs truncate text-zinc-300" title={c.message}>{c.message || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {pagination && (pagination.totalPages > 1 || pagination.totalContacts > 0) && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-zinc-800">
                    <p className="text-zinc-400 text-sm">
                      Page {pagination.currentPage} of {pagination.totalPages} · {pagination.totalContacts} total
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={!pagination.hasPrev || loading}
                        className="rounded-lg bg-zinc-700 px-3 py-1.5 text-sm hover:bg-zinc-600 disabled:opacity-50 disabled:pointer-events-none"
                      >
                        Previous
                      </button>
                      <button
                        type="button"
                        onClick={() => setPage((p) => p + 1)}
                        disabled={!pagination.hasNext || loading}
                        className="rounded-lg bg-zinc-700 px-3 py-1.5 text-sm hover:bg-zinc-600 disabled:opacity-50 disabled:pointer-events-none"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPortal;





